/**
 * The one storage contract every platform implements. Repositories only ever
 * see this interface, so the same SQL runs on iOS/Android (native SQLite),
 * web (SQLite-WASM on OPFS) and in tests (in-memory SQLite-WASM).
 *
 * Writes that must be atomic (an entity row + its sync outbox row) go
 * through `batch`, which runs all statements in one transaction. There is no
 * interactive transaction API on purpose: the web driver lives in a worker
 * and holding a transaction open across async hops invites deadlocks.
 */
export type SqlValue = string | number | null | Uint8Array;
export type SqlParams = readonly SqlValue[];
export type Row = Record<string, SqlValue>;

export interface Statement {
  sql: string;
  params?: SqlParams;
}

export type DriverKind = 'native-sqlite' | 'web-opfs' | 'web-memory' | 'memory';

export interface SqlDriver {
  readonly kind: DriverKind;
  /** True when data survives an app restart. */
  readonly persistent: boolean;
  run(sql: string, params?: SqlParams): Promise<void>;
  all<T extends Row = Row>(sql: string, params?: SqlParams): Promise<T[]>;
  batch(statements: readonly Statement[]): Promise<void>;
  close(): Promise<void>;
  /** Web only: let go of the database so another tab can open it (this page stops using it). */
  release?(): Promise<void>;
}

/**
 * Web: the database is open in another tab, and one copy of it can be in
 * use at a time. `whenFree` resolves with it once that tab closes or hands
 * it over (see app/otherTab.ts).
 */
export class DatabaseInUse extends Error {
  constructor(readonly whenFree: () => Promise<SqlDriver>) {
    super('The database is open in another tab');
    this.name = 'DatabaseInUse';
  }
}

export async function first<T extends Row = Row>(db: SqlDriver, sql: string, params?: SqlParams): Promise<T | undefined> {
  return (await db.all<T>(sql, params))[0];
}

/**
 * Many rows in one value: SQLite packs the result into a single compact JSON
 * array and the rows are rebuilt here. On phones every row otherwise crosses
 * the native bridge as its own object with every column name repeated —
 * 10,000 entries took 7.1 s that way and 0.5 s packed (Phase 26). Works on
 * every driver (it is plain SQL); order is the inner query's.
 */
export async function allValues(db: SqlDriver, sql: string, params: SqlParams | undefined, columns: readonly string[]): Promise<SqlValue[][]> {
  const list = columns.map((c) => `"${c}"`).join(', ');
  const [packed] = await db.all<{ j: string | null }>(`SELECT json_group_array(json_array(${list})) AS j FROM (${sql})`, params);
  return JSON.parse(packed?.j ?? '[]') as SqlValue[][];
}

export async function allRows<T extends Row = Row>(db: SqlDriver, sql: string, params: SqlParams | undefined, columns: readonly string[]): Promise<T[]> {
  const values = await allValues(db, sql, params, columns);
  return values.map((v) => {
    const row: Row = {};
    columns.forEach((c, i) => (row[c] = v[i] ?? null));
    return row as T;
  });
}
